import type { UseQueryResult } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Catalog } from "@/entities/thread";
import { DesktopCatalogGrid } from "@/features/catalog/components/desktop";
import { CatalogItem } from "@/features/catalog/components/lists/CatalogItem";
import { MobileCatalogGridLayout } from "@/features/catalog/components/lists/MobileCatalogGridLayout";
import { useFavoriteThreads } from "@/features/catalog/hooks/useFavoriteThreads";
import { useFilteredThreads } from "@/features/catalog/hooks/useFilteredThreads";
import { useOekakiFloor } from "@/features/catalog/hooks/useOekakiFloor";
import {
  createCatalogContext,
  getLastCatalogIds,
  getNewCatalogThreadIds,
  useCatalogStore,
} from "@/features/catalog/stores/catalogStore";
import { catalogContextBoardSlug } from "@/features/catalog/utils/catalogContext";
import { useIsDesktop } from "@/shared/hooks";
import { Message } from "@/shared/ui/feedback";
import { QueryStateBoundary } from "@/shared/ui/feedback/QueryStateBoundary";

interface CatalogGridProps {
  query: UseQueryResult<Catalog>;
  onDesktopRefresh: () => void;
  wheelReloadEnabled?: boolean;
}

export const CatalogGrid: React.FunctionComponent<CatalogGridProps> = ({
  query,
  onDesktopRefresh,
  wheelReloadEnabled = true,
}: CatalogGridProps) => {
  const isDesktop = useIsDesktop();
  return isDesktop ? (
    <DesktopCatalogGrid
      query={query}
      onRefresh={onDesktopRefresh}
      wheelReloadEnabled={wheelReloadEnabled}
    />
  ) : (
    <MobileCatalogGrid query={query} />
  );
};

interface MobileCatalogGridProps {
  query: UseQueryResult<Catalog>;
}

const MobileCatalogGrid: React.FunctionComponent<MobileCatalogGridProps> = ({
  query,
}: MobileCatalogGridProps) => {
  const { data, error, isFetching, isLoading, refetch } = query;
  const { columns, currentSort, lastCatalogIds } = useCatalogStore();

  const filteredThreads = useFilteredThreads(data?.threads);
  // そうだね閾値を満たすお絵描きスレを中間ラインより下に沈ませない(お気に入りは最上位を維持するため後段で適用)
  const flooredThreads = useOekakiFloor(filteredThreads);
  const visibleThreads = useFavoriteThreads(flooredThreads);

  const newThreadIds = useMemo(() => {
    const context = createCatalogContext(
      catalogContextBoardSlug(),
      // SP版は取得ページを末尾へ統合するため、履歴も先頭ページの文脈へ集約する。
      1,
      currentSort,
    );
    return getNewCatalogThreadIds(
      data?.threads.map((thread) => thread.id) ?? [],
      getLastCatalogIds({ lastCatalogIds }, context),
    );
  }, [currentSort, data?.threads, lastCatalogIds]);

  return (
    <QueryStateBoundary
      error={error}
      hasData={data !== undefined}
      isLoading={isLoading}
      isRetrying={isFetching}
      message="スレッド一覧の読み込みに失敗しました"
      onRetry={(): void => void refetch()}
    >
      {visibleThreads.length === 0 ? (
        <Message variant="info">
          {(data?.threads.length ?? 0) > 0
            ? "検索条件に一致するスレッドが見つかりません"
            : "スレッドが見つかりません"}
        </Message>
      ) : (
        <MobileCatalogGridLayout columns={columns} reserveFooterSpace={false}>
          {visibleThreads.map((thread) => (
            <CatalogItem
              key={thread.id}
              thread={thread}
              isNew={newThreadIds.has(thread.id)}
            />
          ))}
        </MobileCatalogGridLayout>
      )}
    </QueryStateBoundary>
  );
};
